import { useState, useEffect, useMemo } from 'react';

import Button from '~/shared/components/Buttons';
import SearchBar from '~/shared/components/SearchBar';
import Typography from '~/shared/components/Typography';
import Loader from '~/shared/components/Loader';
import { usePermission } from '~/shared/hooks/usePermission';

import { getTreeItem } from '../helpers/treeDataLoader';
import { countNodesInTree } from '../helpers/mergeTreeData';

import HeadlessTreeNode from './HeadlessTreeNode';
import { useConfigurationTree } from './useConfigurationTree';
import AddNewConfigurationModal from './AddNewConfigurationModal';

import type { ItemInstance } from '@headless-tree/core';
import type { ConfigTreeNode } from '~/settings/components/ConfigurationsPage/types';

interface VerticalLineConfig {
  top: number;
  height: number;
  left: number;
  key: string;
}

function calculateVerticalLines(items: ItemInstance<ConfigTreeNode>[]): VerticalLineConfig[] {
  const ITEM_HEIGHT = 64;
  const ITEM_GAP = 4;
  const ITEM_SPACING = ITEM_HEIGHT + ITEM_GAP;
  const CHEVRON_OFFSET = 32;
  const LEVEL_INDENT = 20;
  const BASE_LEFT_OFFSET = 22;

  const lines: VerticalLineConfig[] = [];

  for (let parentIndex = 0; parentIndex < items.length; parentIndex++) {
    const parentItem = items[parentIndex];
    const parentMeta = parentItem.getItemMeta();
    const parentLevel = parentMeta.level;
    const isExpanded = parentItem.isExpanded();
    const hasChildren = parentItem.isFolder();

    if (!isExpanded || !hasChildren) {
      continue;
    }

    let lastChildIndex = -1;
    for (let childIndex = parentIndex + 1; childIndex < items.length; childIndex++) {
      const childItem = items[childIndex];
      const childMeta = childItem.getItemMeta();
      const childLevel = childMeta.level;

      if (childLevel <= parentLevel) {
        break;
      }

      // If this is a direct child (level + 1), track it
      if (childLevel === parentLevel + 1) {
        lastChildIndex = childIndex;
      }
    }

    if (lastChildIndex === -1) {
      continue;
    }

    const top = parentIndex * ITEM_SPACING + CHEVRON_OFFSET;
    const bottom = lastChildIndex * ITEM_SPACING + ITEM_HEIGHT;
    const height = bottom - top;
    const left = parentLevel * LEVEL_INDENT + BASE_LEFT_OFFSET;

    lines.push({
      top,
      height,
      left,
      key: `line-${parentItem.getId()}`,
    });
  }

  return lines;
}

interface ConfigurationTreeProps {
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string) => void;
}

export default function ConfigurationTree({
  selectedNodeId,
  onSelectNode,
}: ConfigurationTreeProps) {
  const canWriteConfig = usePermission('datafy.write.config');
  const { tree, treeData, isLoading, hasData } = useConfigurationTree('edited');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    if (searchQuery.trim()) {
      tree.setSearch(searchQuery);
    } else {
      tree.setSearch(null);
    }
  }, [searchQuery, tree]);

  const allItems = tree.getItems();

  // Manually filter items based on search query
  const items = useMemo(() => {
    if (!searchQuery.trim()) {
      return allItems;
    }

    const searchLower = searchQuery.toLowerCase();
    const matchingItemIds = new Set<string>();
    const itemsToInclude = new Set<string>();

    allItems.forEach((item) => {
      const itemName = item.getItemName().toLowerCase();
      if (itemName.includes(searchLower)) {
        matchingItemIds.add(item.getId());
        itemsToInclude.add(item.getId());
      }
    });

    //Include parent items of matching items to preserve tree structure
    allItems.forEach((item, index) => {
      if (matchingItemIds.has(item.getId())) {
        // This item matches - include all its ancestors
        const itemMeta = item.getItemMeta();
        let currentLevel = itemMeta.level;

        // Walk backwards to find and include all parent items
        for (let i = index - 1; i >= 0; i--) {
          const potentialParent = allItems[i];
          const parentMeta = potentialParent.getItemMeta();
          const parentLevel = parentMeta.level;

          // If this is the immediate parent (level is exactly one less)
          if (parentLevel === currentLevel - 1) {
            itemsToInclude.add(potentialParent.getId());
            currentLevel = parentLevel;
            // Continue up the tree
            if (parentLevel === 0) {
              // Reached root, stop
              break;
            }
          } else if (parentLevel >= currentLevel) {
            // We've gone past the parent chain, stop
            break;
          }
        }
      }
    });

    // Filter items to only include matching items and their parents
    const filtered = allItems.filter((item) => itemsToInclude.has(item.getId()));

    return filtered;
  }, [allItems, searchQuery]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleAddClick = () => {
    setShowAddModal(true);
  };

  const handleModalClose = () => {
    setShowAddModal(false);
  };

  const handleModalNext = (selectedNodeId: string) => {
    // TODO: Implement next step logic (e.g., open configuration form for selected item)
    setShowAddModal(false);
  };

  return (
    <div className="flex flex-col h-full py-4 px-5">
      <div className="py-2">
        <SearchBar
          size="sm"
          placeholder="Search"
          value={searchQuery}
          onChange={handleSearchChange}
          className="w-full"
        />
      </div>

      {canWriteConfig && (
        <div className="py-2">
          <Button variant="primary" className="w-full" onClick={handleAddClick}>
            <span className="text-[22px] leading-none -mt-1">+</span> Add new configuration
          </Button>
        </div>
      )}

      <div
        className="flex-1 min-h-0 overflow-y-auto side-panel-scrollbar"
        style={{ direction: 'rtl' }}
        {...tree.getContainerProps()}
      >
        <div style={{ direction: 'ltr' }}>
          {items.length === 0 ? (
            isLoading && !hasData ? (
              <div className="flex flex-1 justify-center items-center h-full">
                <Loader variant="xl" />
              </div>
            ) : (
              <div className="p-4 text-center text-primary-900">
                <Typography variant="p2">No items found</Typography>
              </div>
            )
          ) : (
            <div className="flex flex-col gap-1 relative">
              {calculateVerticalLines(items).map(({ top, height, left, key }) => (
                <div
                  key={key}
                  className="absolute w-[1px] bg-primary-100 z-20"
                  style={{
                    left: `${left}px`,
                    top: `${top}px`,
                    height: `${height}px`,
                  }}
                />
              ))}
              {items.map((item) => {
                const node =
                  treeData && item.isFolder() ? getTreeItem(item.getId(), treeData) : undefined;
                const stableCount = node !== undefined ? countNodesInTree(node) - 1 : undefined;
                return (
                  <HeadlessTreeNode
                    key={item.getId()}
                    item={item}
                    selectedNodeId={selectedNodeId}
                    onSelect={onSelectNode}
                    hasChevron={item.isFolder()}
                    count={item.isFolder() ? stableCount : undefined}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AddNewConfigurationModal
        isOpen={showAddModal}
        onClose={handleModalClose}
        onNext={handleModalNext}
      />
    </div>
  );
}
